<template>
  <div class="mindmap-sidebar">
    <!-- Tabs -->
    <div class="sidebar-tabs">
      <button
        class="tab-button"
        :class="{ active: activeTab === 'prompts' }"
        @click="activeTab = 'prompts'"
      >
        <i class="fa fa-lightbulb me-1"></i>
        Prompts
      </button>
      <button
        class="tab-button"
        :class="{ active: activeTab === 'properties' }"
        @click="activeTab = 'properties'"
      >
        <i class="fa fa-info-circle me-1"></i>
        Properties
      </button>
      <button
        class="tab-button"
        :class="{ active: activeTab === 'chat' }"
        @click="activeTab = 'chat'"
      >
        <i class="fa fa-robot me-1"></i>
        AI Chat
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Prompts Tab -->
      <div
        v-show="activeTab === 'prompts'"
        class="tab-pane"
        :class="{ 'active-pane': activeTab === 'prompts' }"
      >
        <PromptPanel :isActive="activeTab === 'prompts'" />
      </div>

      <!-- Properties Tab -->
      <div
        v-show="activeTab === 'properties'"
        class="tab-pane"
        :class="{ 'active-pane': activeTab === 'properties' }"
      >
        <div v-if="!selectedNode" class="no-selection">
          <i class="fa fa-hand-pointer fa-3x text-muted mb-3"></i>
          <p class="text-muted">Select a node to view its properties</p>
        </div>

        <div v-else class="properties-container">
          <div class="property-header">
            <h6 class="mb-0">Node Properties</h6>
            <div class="d-flex gap-2">
              <button
                v-if="isCategory(selectedNode)"
                class="btn btn-sm btn-success"
                @click="handleAddChildNode"
                title="Add child node"
              >
                <i class="fa fa-plus me-1"></i>
                Add Child
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="$emit('close-properties')"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>

          <div class="properties-list">
            <!-- Title (editable) -->
            <div class="property-item">
              <label class="property-label">Title</label>
              <div v-if="!isEditingTitle" class="property-value">
                <div class="description-display">
                  <span>{{ selectedNode.title }}</span>
                  <button
                    class="btn btn-sm btn-link text-primary ms-2"
                    @click="startEditTitle"
                    title="Edit title"
                  >
                    <i class="fa fa-edit"></i>
                  </button>
                </div>
              </div>
              <div v-else class="property-value">
                <input
                  v-model="editedTitle"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Enter title..."
                  @keyup.enter="saveTitle"
                  @keyup.escape="cancelEditTitle"
                />
                <div class="d-flex gap-2 mt-2">
                  <button
                    class="btn btn-sm btn-success"
                    @click="saveTitle"
                    :disabled="isSavingTitle"
                  >
                    <i class="fa fa-check me-1"></i>
                    {{ isSavingTitle ? "Saving..." : "Save" }}
                  </button>
                  <button
                    class="btn btn-sm btn-secondary"
                    @click="cancelEditTitle"
                    :disabled="isSavingTitle"
                  >
                    <i class="fa fa-times me-1"></i>
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <!-- Slug (if available) -->
            <div v-if="selectedNode.slug" class="property-item">
              <label class="property-label">Slug</label>
              <div class="property-value">
                <span class="badge bg-secondary">{{ selectedNode.slug }}</span>
              </div>
            </div>

            <!-- Description (editable) -->
            <div class="property-item">
              <label class="property-label">Description</label>
              <div v-if="!isEditingDescription" class="property-value">
                <div class="description-display">
                  <span v-if="selectedNode.description">{{
                    selectedNode.description
                  }}</span>
                  <span v-else class="text-muted">No description</span>
                  <button
                    class="btn btn-sm btn-link text-primary ms-2"
                    @click="startEditDescription"
                    title="Edit description"
                  >
                    <i class="fa fa-edit"></i>
                  </button>
                </div>
              </div>
              <div
                v-else
                class="property-value"
                style="align-items: end; flex-direction: column"
              >
                <textarea
                  v-model="editedDescription"
                  class="form-control form-control-sm"
                  rows="6"
                  placeholder="Enter description..."
                  style="width: 100%; resize: vertical"
                ></textarea>
                <div class="d-flex gap-2 mt-2">
                  <button
                    class="btn btn-sm btn-success"
                    @click="saveDescription"
                    :disabled="isSaving"
                  >
                    <i class="fa fa-check me-1"></i>
                    {{ isSaving ? "Saving..." : "Save" }}
                  </button>
                  <button
                    class="btn btn-sm btn-secondary"
                    @click="cancelEditDescription"
                    :disabled="isSaving"
                  >
                    <i class="fa fa-times me-1"></i>
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <!-- Type -->
            <div class="property-item">
              <label class="property-label">Type</label>
              <div class="property-value">
                <span
                  class="badge"
                  :class="
                    isCategory(selectedNode)
                      ? 'bg-primary'
                      : selectedNode.isRoot
                      ? 'bg-info'
                      : 'bg-success'
                  "
                >
                  {{
                    isCategory(selectedNode)
                      ? "Category Node"
                      : selectedNode.isRoot
                      ? "Root Node"
                      : "Child Node"
                  }}
                </span>
              </div>
            </div>

            <!-- Background Color (editable) -->
            <div class="property-item">
              <label class="property-label">Background Color</label>
              <div class="property-value">
                <div class="color-picker-wrapper">
                  <input
                    type="color"
                    :value="selectedNode.color"
                    @input="handleColorChange($event)"
                    class="color-input"
                    title="Click to change background color"
                  />
                  <div
                    class="color-preview"
                    :style="{ backgroundColor: selectedNode.color }"
                  ></div>
                  <span class="text-muted small ms-2">{{
                    selectedNode.color
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Text Color (editable) -->
            <div class="property-item">
              <label class="property-label">Text Color</label>
              <div class="property-value">
                <div class="color-picker-wrapper">
                  <input
                    type="color"
                    :value="selectedNode.forecolor || '#212529'"
                    @input="handleForecolorChange($event)"
                    class="color-input"
                    title="Click to change text color"
                  />
                  <div
                    class="color-preview"
                    :style="{
                      backgroundColor: selectedNode.forecolor || '#212529',
                    }"
                  ></div>
                  <span class="text-muted small ms-2">{{
                    selectedNode.forecolor || "#212529"
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Chat Tab -->
      <div
        v-show="activeTab === 'chat'"
        class="tab-pane chat-pane"
        :class="{ 'active-pane': activeTab === 'chat' }"
      >
        <div class="ai-chat-container">
          <div class="ai-chat-header">
            <div class="ai-chat-title">
              <div class="ai-logo-small">
                <i class="fa fa-robot"></i>
              </div>
              <div class="ms-2">
                <div class="fw-bold text-white small">AI Assistant</div>
                <div class="text-muted extra-small">{{ selectedModel }}</div>
              </div>
            </div>
            <div class="ai-chat-badges">
              <div v-if="isRunningPrompt" class="streaming-dot-wrapper">
                <span class="streaming-dot"></span>
              </div>
              <div v-if="responseTokenCount > 0" class="token-count-pill">
                {{ responseTokenCount }} tokens
              </div>
            </div>
          </div>

          <div class="ai-chat-content" ref="chatContentRef">
            <div v-if="!aiResponse && !isRunningPrompt" class="chat-empty-state">
              <div class="empty-icon">
                <i class="fa fa-comments"></i>
              </div>
              <h6>No conversation yet</h6>
              <p>Generate a prompt and click "Run Prompt" to start a chat with AI.</p>
              <button class="btn btn-primary btn-sm mt-3" @click="$emit('run-prompt')">
                <i class="fa fa-play me-1"></i> Run Prompt
              </button>
            </div>

            <div v-else class="chat-messages">
              <div class="ai-message-bubble">
                <div class="ai-message-header">
                  <span><i class="fa fa-robot me-1"></i> AI Response</span>
                  <div class="ai-message-actions">
                    <button v-if="aiResponse" class="action-icon-btn" @click="$emit('copy-ai-response')" title="Copy response">
                      <i class="fa fa-copy"></i>
                    </button>
                  </div>
                </div>
                <div class="ai-message-body" v-html="formattedAiResponse"></div>
                <span v-if="isRunningPrompt" class="streaming-cursor">▌</span>
                
                <div v-if="!aiResponse && isRunningPrompt" class="ai-thinking-small">
                  <div class="thinking-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="ai-chat-footer">
            <button 
              v-if="isRunningPrompt" 
              class="btn btn-danger btn-sm w-100" 
              @click="$emit('stop-prompt')"
            >
              <i class="fa fa-stop me-1"></i> Stop Generating
            </button>
            <div v-else class="chat-footer-actions">
              <button 
                class="btn btn-outline-secondary btn-sm flex-grow-1" 
                @click="$emit('clear-ai-response')"
                :disabled="!aiResponse"
              >
                <i class="fa fa-trash me-1"></i> Clear
              </button>
              <button 
                class="btn btn-primary btn-sm flex-grow-1" 
                @click="$emit('run-prompt')"
                :disabled="isRunningPrompt"
              >
                <i class="fa fa-refresh me-1"></i> Regenerate
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed, nextTick } from "vue";
import PromptPanel from "./PromptPanel.vue";
import { useMindmapStore } from "@/store/mindmap";
import apiService from "@/services/apiService";

const mindmapStore = useMindmapStore();

// Props
const props = defineProps<{
  selectedNode: any;
  aiResponse?: string;
  isRunningPrompt?: boolean;
  responseTokenCount?: number;
  selectedModel?: string;
}>();

// Emits
const emit = defineEmits([
  "close-properties", 
  "add-child-node", 
  "run-prompt", 
  "stop-prompt", 
  "clear-ai-response", 
  "copy-ai-response"
]);

// State
const activeTab = ref<"prompts" | "properties" | "chat">("prompts");
const isEditingTitle = ref(false);
const editedTitle = ref("");
const isSavingTitle = ref(false);
const isEditingDescription = ref(false);
const editedDescription = ref("");
const isSaving = ref(false);
const chatContentRef = ref<HTMLDivElement | null>(null);

// Watch for node selection - auto switch to properties tab when node is selected
watch(
  () => props.selectedNode,
  (newNode) => {
    // Only auto-switch to properties when a node is selected
    // Don't auto-switch back to prompts when deselected (let user control tabs)
    if (newNode) {
      activeTab.value = "properties";
    }
  }
);

// Watch for AI response changes to scroll chat
watch(
  () => props.aiResponse,
  async () => {
    if (activeTab.value === 'chat' && chatContentRef.value) {
      await nextTick();
      chatContentRef.value.scrollTop = chatContentRef.value.scrollHeight;
    }
  }
);

// Expose method to set active tab
const setActiveTab = (tab: "prompts" | "properties" | "chat") => {
  activeTab.value = tab;
};

defineExpose({ setActiveTab });

// Helper to escape HTML for code blocks
const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Format AI markdown-like response to HTML (copied from Mindmap.vue logic)
const formattedAiResponse = computed(() => {
  if (!props.aiResponse) return "";
  let text = props.aiResponse;

  // Code blocks (``` ... ```)
  text = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre class="ai-code-block-small"><code class="lang-${lang || 'text'}">${escapeHtml(code.trim())}</code></pre>`;
  });

  // Inline code
  text = text.replace(/`([^`]+)`/g, '<code class="ai-inline-code-small">$1</code>');

  // Bold (**text**)
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic (*text*)
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Headers (### ## #)
  text = text.replace(/^### (.+)$/gm, '<h6 class="ai-h6">$1</h6>');
  text = text.replace(/^## (.+)$/gm, '<h5 class="ai-h5">$1</h5>');
  text = text.replace(/^# (.+)$/gm, '<h4 class="ai-h4">$1</h4>');

  // Unordered lists
  text = text.replace(/^[-*] (.+)$/gm, '<li>$1</li>');
  text = text.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

  // Numbered lists
  text = text.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Line breaks
  text = text.replace(/\n\n/g, '</p><p>');
  text = text.replace(/\n/g, '<br/>');
  text = `<p>${text}</p>`;

  return text;
});

// Helper to check if node is a category (based on description pattern)
const isCategory = (node: any) => {
  return node.description && node.description.includes("Category with");
};

// Format date
const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Handle color change
const handleColorChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newColor = target.value;
  if (props.selectedNode) {
    mindmapStore.updateNodeColor(props.selectedNode.id, newColor);
  }
};

// Handle forecolor change
const handleForecolorChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newColor = target.value;
  if (props.selectedNode) {
    mindmapStore.updateNodeForecolor(props.selectedNode.id, newColor);
  }
};

// Start editing title
const startEditTitle = () => {
  if (props.selectedNode) {
    editedTitle.value = props.selectedNode.title || "";
    isEditingTitle.value = true;
  }
};

// Cancel title editing
const cancelEditTitle = () => {
  isEditingTitle.value = false;
  editedTitle.value = "";
};

// Save title
const saveTitle = async () => {
  if (!props.selectedNode || !editedTitle.value.trim()) return;

  isSavingTitle.value = true;
  try {
    // Check if it's the Start node (special UI-only node)
    const isStartNode =
      props.selectedNode.id === "start_node" ||
      props.selectedNode.variableId === "start";

    if (!isStartNode && props.selectedNode.variableId) {
      // Only save to database if it's not the Start node
      const endpoint = isCategory(props.selectedNode)
        ? "/common/prompt-categories"
        : "/common/prompt-variables";

      await apiService.put(endpoint, props.selectedNode.variableId, {
        title: editedTitle.value.trim(),
      });
    }

    // Update local node (works for both Start node and database nodes)
    mindmapStore.updateNodeTitle(
      props.selectedNode.id,
      editedTitle.value.trim()
    );
    isEditingTitle.value = false;
  } catch (error: any) {
    console.error("Error saving title:", error);
    alert(error?.response?.data?.message || "Failed to save title");
  } finally {
    isSavingTitle.value = false;
  }
};

// Start editing description
const startEditDescription = () => {
  if (props.selectedNode) {
    editedDescription.value = props.selectedNode.description || "";
    isEditingDescription.value = true;
  }
};

// Cancel editing
const cancelEditDescription = () => {
  isEditingDescription.value = false;
  editedDescription.value = "";
};

// Save description
const saveDescription = async () => {
  if (!props.selectedNode) return;

  isSaving.value = true;
  try {
    // Check if it's the Start node (special UI-only node)
    const isStartNode =
      props.selectedNode.id === "start_node" ||
      props.selectedNode.variableId === "start";

    if (!isStartNode && props.selectedNode.variableId) {
      // Only save to database if it's not the Start node
      const endpoint = isCategory(props.selectedNode)
        ? "/common/prompt-categories"
        : "/common/prompt-variables";

      await apiService.put(endpoint, props.selectedNode.variableId, {
        description: editedDescription.value,
      });
    }

    // Update local node (works for both Start node and database nodes)
    mindmapStore.updateNodeDescription(
      props.selectedNode.id,
      editedDescription.value
    );
    isEditingDescription.value = false;
  } catch (error: any) {
    console.error("Error saving description:", error);
    alert(error?.response?.data?.message || "Failed to save description");
  } finally {
    isSaving.value = false;
  }
};

// Add child node
const handleAddChildNode = () => {
  if (props.selectedNode) {
    emit("add-child-node", props.selectedNode.id);
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.mindmap-sidebar {
  width: 340px;
  height: 100%;
  background: #fff;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== TABS ===== */
.sidebar-tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  padding: 0 0.5rem;
  gap: 0.25rem;
  padding-top: 0.35rem;
}

.tab-button {
  flex: 1;
  padding: 0.6rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  color: #6b7280;
  transition: all 0.2s ease;
  position: relative;
  letter-spacing: 0.01em;
}

.tab-button:hover {
  color: #374151;
  background: rgba(99, 102, 241, 0.05);
}

.tab-button.active {
  color: #6366f1;
  background: #fff;
}

.tab-button.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 2px 2px 0 0;
}

/* ===== TAB CONTENT ===== */
.tab-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tab-pane {
  flex: 1;
  overflow: hidden;
  display: none;
  flex-direction: column;
  height: 100%;
}

.tab-pane.active-pane {
  display: flex;
}

/* ===== NO SELECTION STATE ===== */
.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
  gap: 0.75rem;
}

.no-selection i {
  font-size: 3rem;
  color: #d1d5db;
}

.no-selection p {
  color: #9ca3af;
  font-size: 0.85rem;
  margin: 0;
}

/* ===== PROPERTIES CONTAINER ===== */
.properties-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.property-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to right, #f9fafb, #f3f4f6);
}

.property-header h6 {
  font-weight: 700;
  color: #111827;
  font-size: 0.875rem;
  margin: 0;
}

.properties-list {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.property-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.75rem;
  transition: border-color 0.15s;
}

.property-item:last-child {
  border-bottom: 1px solid #e5e7eb;
}

.property-item:focus-within {
  border-color: #a5b4fc;
  background: #fff;
}

.property-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #6366f1;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
  display: block;
  letter-spacing: 0.06em;
}

.property-value {
  font-size: 0.85rem;
  color: #111827;
  word-break: break-word;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.description-display {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  width: 100%;
}

.description-display span {
  flex: 1;
  word-break: break-word;
}

/* ===== COLOR PICKERS ===== */
.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.color-preview:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.color-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  position: relative;
}

.color-input {
  position: absolute;
  opacity: 0;
  width: 28px;
  height: 28px;
  cursor: pointer;
}

.color-input:hover + .color-preview {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* ===== SCROLLBAR ===== */
.properties-list::-webkit-scrollbar {
  width: 4px;
}

.properties-list::-webkit-scrollbar-track {
  background: transparent;
}

.properties-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.properties-list::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}

/* ===== AI CHAT STYLES ===== */
.ai-chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #0f1117;
  overflow: hidden;
}

.ai-chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1rem;
  background: linear-gradient(135deg, #1a1f36 0%, #1e2030 100%);
  border-bottom: 1px solid #2d3561;
}

.ai-chat-title {
  display: flex;
  align-items: center;
}

.ai-logo-small {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.75rem;
}

.ai-chat-badges {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.token-count-pill {
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  padding: 0.1rem 0.5rem;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: 600;
  border: 1px solid rgba(99, 102, 241, 0.3);
}

.ai-chat-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.chat-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: #6b7280;
  padding: 1rem;
}

.empty-icon {
  font-size: 2.5rem;
  color: #1e293b;
  margin-bottom: 1rem;
}

.chat-empty-state h6 {
  color: #e2e8f0;
  margin-bottom: 0.5rem;
}

.chat-empty-state p {
  font-size: 0.8rem;
}

.ai-message-bubble {
  background: #1e293b;
  border-radius: 12px;
  padding: 0.85rem;
  border: 1px solid #334155;
  color: #f1f5f9;
}

.ai-message-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ai-message-body {
  font-size: 0.85rem;
  line-height: 1.6;
}

.ai-message-body :deep(p) {
  margin-bottom: 0.75rem;
}

.ai-message-body :deep(p:last-child) {
  margin-bottom: 0;
}

.action-icon-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.2rem;
  transition: color 0.2s;
}

.action-icon-btn:hover {
  color: #6366f1;
}

.ai-chat-footer {
  padding: 1rem;
  background: #0f1117;
  border-top: 1px solid #1e293b;
}

.chat-footer-actions {
  display: flex;
  gap: 0.5rem;
}

.extra-small {
  font-size: 0.65rem;
}

/* AI response formatting in sidebar */
:deep(.ai-code-block-small) {
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 0.6rem;
  margin: 0.5rem 0;
  overflow-x: auto;
  font-family: 'Fira Code', monospace;
  font-size: 0.72rem;
  color: #e6edf3;
}

:deep(.ai-inline-code-small) {
  background: rgba(99, 102, 241, 0.15);
  border-radius: 4px;
  padding: 0.1em 0.3em;
  font-family: monospace;
  font-size: 0.9em;
  color: #a5b4fc;
}

.streaming-cursor {
  display: inline-block;
  color: #10b981;
  animation: blink-cursor 0.8s step-end infinite;
}

@keyframes blink-cursor {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.ai-thinking-small {
  display: flex;
  justify-content: flex-start;
  padding-top: 0.5rem;
}

.thinking-dots {
  display: flex;
  gap: 0.3rem;
}

.thinking-dots span {
  width: 6px;
  height: 6px;
  background: #6366f1;
  border-radius: 50%;
  animation: thinking-bounce 1.4s ease-in-out infinite;
  opacity: 0.6;
}

.thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
.thinking-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes thinking-bounce {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
}

.streaming-dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  animation: blink-dot 1s ease-in-out infinite;
}

@keyframes blink-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.8); }
}

.ai-chat-content::-webkit-scrollbar {
  width: 4px;
}

.ai-chat-content::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 2px;
}
</style>
